import { Case, Switch } from "@/shared/components/control";
import { site } from "@/config/site";
import { PublicPageLayout } from "@/shared/components/layout/public-page-layout";
import { useTranslation } from "react-i18next";
import { Section, TextLink } from "@/shared/components/ui";
import { BorderScale } from "./components/border-scale";
import { ButtonShowcase } from "./components/button-showcase";
import { SurfaceStack } from "./components/surface-stack";
import { TokenSwatchGrid } from "./components/token-swatch-grid";
import { TypeScale } from "./components/type-scale";
import { useDesignSystemContainer } from "./container";

const TAB_IDS = ["colors", "typography", "components"] as const;

export function DesignSystemPage() {
  const { t } = useTranslation();
  const { tabs } = useDesignSystemContainer();

  return (
    <PublicPageLayout>
      <header className="sticky top-0 z-50 border-b border-border-soft bg-canvas/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-4 px-4 py-4 sm:px-6 lg:px-8">
          <div>
            <p className="font-mono text-[0.65rem] uppercase tracking-widest text-ink-tertiary">
              {t("designPage.subtitle")}
            </p>
            <h1 className="font-display text-xl font-semibold text-ink">
              {t("designPage.title")}
            </h1>
          </div>
          <TextLink
            to={site.links.homePath}
            className="normal-case tracking-[0.12em]"
          >
            {t("designPage.backLink")}
          </TextLink>
        </div>
        <div
          className="mx-auto flex max-w-5xl gap-2 border-t border-border-soft px-4 py-2 sm:px-6 lg:px-8"
          role="tablist"
          aria-label={t("designPage.tablistAriaLabel")}
        >
          {TAB_IDS.map(id => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={tabs.tab === id}
              className={`rounded-md px-3 py-2 font-mono text-[0.65rem] uppercase tracking-widest transition-colors ${
                tabs.tab === id
                  ? "bg-surface-2 text-accent"
                  : "text-ink-tertiary hover:text-ink-secondary"
              }`}
              onClick={() => {
                tabs.select(id);
              }}
            >
              {t(`designPage.tabs.${id}`)}
            </button>
          ))}
        </div>
      </header>

      <main className="pb-24">
        <Section className="border-b border-border-soft bg-surface-1/20">
          <p className="max-w-prose text-sm font-normal leading-relaxed text-ink-secondary">
            {t("designPage.intro")}
          </p>
        </Section>

        <Section>
          <Switch value={tabs.tab}>
            <Case value="colors">
              <div className="space-y-8">
                <h2 className="font-display text-2xl font-semibold text-ink">
                  {t("designPage.sections.colors.title")}
                </h2>
                <TokenSwatchGrid />
                <h3 className="font-display text-lg font-semibold text-ink">
                  {t("designPage.sections.colors.surfaces")}
                </h3>
                <SurfaceStack />
                <h3 className="font-display text-lg font-semibold text-ink">
                  {t("designPage.sections.colors.borders")}
                </h3>
                <BorderScale />
              </div>
            </Case>
            <Case value="typography">
              <div className="space-y-6">
                <h2 className="font-display text-2xl font-semibold text-ink">
                  {t("designPage.sections.typography.title")}
                </h2>
                <TypeScale />
              </div>
            </Case>
            <Case value="components">
              <div className="space-y-6">
                <h2 className="font-display text-2xl font-semibold text-ink">
                  {t("designPage.sections.components.title")}
                </h2>
                <ButtonShowcase />
              </div>
            </Case>
          </Switch>
        </Section>
      </main>
    </PublicPageLayout>
  );
}
